import { NotificationGroup } from '@/shared/types/message';
import { NotificationItem } from './NotificationItem';
import { useMessageStyles } from '../../hooks/useMessageStyles';
import { notificationService } from '../../services/NotificationService';
import React from 'react';

/** Estado de mazos de notificación (claves internas → grupo con cola y metadatos). */
export interface NotificationPositionsProps {
  /** Grupos procedentes de `useNotification().groups` o del servicio (`notificationService.getGroups()`). */
  groups: Record<string, NotificationGroup>;
}

/**
 * Un único ancla en pantalla según `notificationService` y un `NotificationItem` por mazo activo.
 *
 * @example
 * import { NotificationPositions, useNotification } from '@imj_media/ui';
 *
 * function Demo() {
 *   const { groups } = useNotification();
 *   return <NotificationPositions groups={groups} />;
 * }
 */
export const NotificationPositions = React.memo(({ groups }: NotificationPositionsProps) => {
  const { getPositionClasses } = useMessageStyles();
  const config = notificationService.getConfig();

  const activeGroups = Object.values(groups);

  if (activeGroups.length === 0) {
    return null;
  }

  const position = config.position;

  if (
    process.env.NODE_ENV !== 'production' &&
    ![
      'top-left',
      'top-center',
      'top-right',
      'center-left',
      'center',
      'center-right',
      'bottom-left',
      'bottom-center',
      'bottom-right',
    ].includes(position)
  ) {
    // eslint-disable-next-line no-console
    console.warn(
      `[NotificationPositions] Invalid position "${position}" from config. Using as-is.`
    );
  }

  return (
    <div className={`${getPositionClasses(position)} ui-z-50 ui-space-y-2 ui-overflow-visible`}>
      {activeGroups.map((group) => {
        if (!group.notification.id) {
          if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.warn('[NotificationPositions] Group notification without id. Skipping.');
          }
          return null;
        }
        // key estable por stackKey (mazo explícito o `auto`)
        return <NotificationItem key={group.stackKey} group={group} />;
      })}
    </div>
  );
});

NotificationPositions.displayName = 'NotificationPositions';
